import React from 'react';
import type { ReactElement } from 'react';
import { createRendererToString, Text } from '@wix/cli-ui-kit';

interface Logger {
  log: (msg: string | ReactElement) => void;
  error: (msg: string | ReactElement) => void;
}

const dateTimeFormat = new Intl.DateTimeFormat([], {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

interface CreateAstroLoggerOptions {
  isPrefixed: boolean;
  onLog?: (msg: string) => void;
  onError?: (msg: string) => void;
}

export function createAstroLogger(options: CreateAstroLoggerOptions): Logger {
  const { isPrefixed, onLog, onError } = options;
  const renderer = createRendererToString({ stripAnsi: false });

  const renderPrefix = () => {
    return renderer.render(
      <Text>
        <Text>{dateTimeFormat.format(new Date())} </Text>
        <Text skin="info">[wix] </Text>
      </Text>
    );
  };

  const formatMessage = (options: {
    isPrefixed: boolean;
    msg: string | ReactElement;
  }) => {
    const { isPrefixed, msg } = options;
    const prefix = isPrefixed ? renderPrefix() : '';
    const suffix = typeof msg === 'string' ? msg : renderer.render(msg);
    return `${prefix}${suffix}`;
  };

  const log = (msg: string | ReactElement) => {
    const stringToLog = formatMessage({ isPrefixed, msg });
    onLog?.(stringToLog);
    // Defer so terminal output order is stable with other sync logs.
    // eslint-disable-next-line no-console
    setTimeout(() => console.log(stringToLog), 0);
  };

  const error = (msg: string | ReactElement) => {
    const stringToLog = formatMessage({
      isPrefixed,
      msg: typeof msg === 'string' ? <Text skin="error">{msg}</Text> : msg,
    });

    onError?.(stringToLog);
    // Defer so terminal output order is stable with other sync logs.
    // eslint-disable-next-line no-console
    setTimeout(() => console.error(stringToLog), 0);
  };

  return { log, error };
}
